import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../lib/api';
import usePageTitle from '../../lib/usePageTitle';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Building2, Save } from 'lucide-react';

export default function AdminEmpresasPage() {
    usePageTitle('Gestión de Empresas');
    const [empresas, setEmpresas] = useState([]);
    const [modulos, setModulos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});

    const load = useCallback(async () => {
        try {
            const [empRes, modRes] = await Promise.all([
                adminAPI.getEmpresas(),
                adminAPI.getModulos(),
            ]);
            setEmpresas(empRes.data);
            setModulos(modRes.data);
        } catch (err) {
            toast.error('Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const activeModulos = modulos.filter(m => m.activo_global);

    const handleModuloToggle = (empresaId, moduloCodigo) => {
        setEmpresas(prev => prev.map(emp => {
            if (emp.id !== empresaId) return emp;
            const current = emp.modulos_asignados || [];
            const next = current.includes(moduloCodigo)
                ? current.filter(c => c !== moduloCodigo)
                : [...current, moduloCodigo];
            return { ...emp, modulos_asignados: next };
        }));
    };

    const handleSave = async (empresaId) => {
        const emp = empresas.find(e => e.id === empresaId);
        if (!emp) return;
        setSaving(prev => ({ ...prev, [empresaId]: true }));
        try {
            await adminAPI.updateEmpresaModulos(empresaId, emp.modulos_asignados || []);
            toast.success(`Módulos de ${emp.nombre_corto || emp.nombre} actualizados`);
        } catch (err) {
            toast.error('Error guardando módulos');
        } finally {
            setSaving(prev => ({ ...prev, [empresaId]: false }));
        }
    };

    if (loading) return <div className="p-8 text-muted-foreground">Cargando empresas...</div>;

    return (
        <div className="max-w-5xl mx-auto" data-testid="admin-empresas-page">
            <div className="mb-6">
                <h1 className="text-2xl font-bold">Gestión de Empresas</h1>
                <p className="text-muted-foreground">Administre empresas y sus módulos asignados.</p>
            </div>

            <div className="space-y-4">
                {empresas.map((emp) => (
                    <div key={emp.id} className="p-5 border rounded-lg bg-card" data-testid={`empresa-${emp.subdominio}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Building2 className="h-6 w-6 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg">{emp.nombre}</h3>
                                    <p className="text-sm text-muted-foreground">
                                        {emp.nombre_corto && <span className="font-medium">{emp.nombre_corto}</span>}
                                        {' '}&middot; {emp.subdominio} &middot; {emp.rut}
                                    </p>
                                </div>
                            </div>
                            <Button
                                onClick={() => handleSave(emp.id)}
                                disabled={saving[emp.id]}
                                size="sm"
                                data-testid={`save-${emp.subdominio}`}
                            >
                                <Save className="h-4 w-4 mr-1" />
                                {saving[emp.id] ? 'Guardando...' : 'Guardar'}
                            </Button>
                        </div>

                        <div className="border-t pt-3">
                            <p className="text-sm font-medium mb-2">Módulos asignados:</p>
                            <div className="flex flex-wrap gap-4">
                                {activeModulos.map((mod) => {
                                    const checked = (emp.modulos_asignados || []).includes(mod.codigo);
                                    return (
                                        <label key={mod.codigo} className="flex items-center gap-2 cursor-pointer">
                                            <Checkbox
                                                checked={checked}
                                                onCheckedChange={() => handleModuloToggle(emp.id, mod.codigo)}
                                                data-testid={`check-${emp.subdominio}-${mod.codigo}`}
                                            />
                                            <span className="text-sm">{mod.nombre}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            {activeModulos.length === 0 && (
                                <p className="text-sm text-muted-foreground">No hay módulos activos globalmente.</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
